// FILE: src/components/studyplan/WeeklyPlan.js
//
// "This Week's Plan" — day groups that hold MULTIPLE mixed task types
// (reading / practice / review / simulation / logistics / rest / exam_day).
// Today's row is highlighted. Practice-family items keep the existing
// onStart launch flow; reading/logistics items toggle completion, which
// also checks off the linked learner_prep_tasks row (linked_task).

import React, { useState } from 'react';
import styles from './StudyPlanViewPage.module.css';
import { setActivityStatus } from '../../services/studyPlanService';
import { setLogisticsDone } from '../../services/examLogisticsService';
import {
  TYPE_CHIP, toPlanItem, todayIso,
  formatWeekRange, dayShortName, dayOfMonth,
} from './studyPlanHelpers';

function groupByDay(activities) {
  const byDate = new Map();
  for (const a of activities) {
    if (!a.scheduled_for) continue;
    if (!byDate.has(a.scheduled_for)) byDate.set(a.scheduled_for, []);
    byDate.get(a.scheduled_for).push(toPlanItem(a));
  }
  return [...byDate.entries()].sort((x, y) => x[0].localeCompare(y[0]));
}

export default function WeeklyPlan({ activities, examId, onStart, onActivityChange }) {
  const days = groupByDay(activities);
  const today = todayIso();
  const all = days.flatMap(([, items]) => items);
  const doneCount = all.filter((i) => i.status === 'completed').length;
  const pct = all.length ? Math.round((100 * doneCount) / all.length) : 0;

  return (
    <section className={styles.card} data-testid="study-plan-view-week">
      <div className={styles.cardHeader}>
        <h2 className={styles.weekTitle}>This Week&rsquo;s Plan</h2>
        <span className={styles.weekRange}>{formatWeekRange(new Date())}</span>
      </div>
      {days.length === 0 ? (
        <p className={styles.emptyHint}>Nothing scheduled this week.</p>
      ) : days.map(([date, items]) => (
        <div key={date} className={`${styles.day} ${date === today ? styles.dayToday : ''}`}>
          <div className={styles.dayCell}>
            <div className={styles.dayName}>{dayShortName(date)}</div>
            <div className={styles.dayNum}>{dayOfMonth(date)}</div>
            {date === today ? <span className={styles.todayTag}>TODAY</span> : null}
          </div>
          <div className={styles.dayTasks}>
            {items.map((item) => (
              <TaskRow key={item.id} item={item} examId={examId} onStart={onStart} onActivityChange={onActivityChange} />
            ))}
          </div>
        </div>
      ))}
      <div className={styles.weekFoot}>
        <span>Weekly progress</span>
        <div className={styles.progressBar}><span style={{ width: pct + '%' }} /></div>
        <span><b className={styles.weekFootCount}>{doneCount} / {all.length}</b> tasks</span>
      </div>
    </section>
  );
}

function TaskRow({ item, examId, onStart, onActivityChange }) {
  const [busy, setBusy] = useState(false);
  const completed = item.status === 'completed';
  const chip = TYPE_CHIP[item.taskType] || item.taskType;
  const meta = [
    item.questionCount ? `${item.questionCount} questions` : null,
    item.estimatedMinutes ? `${item.estimatedMinutes} min` : null,
    item.sourceReference || item.focusPhase || null,
  ].filter(Boolean).join(' · ');

  async function toggle() {
    setBusy(true);
    const nextStatus = completed ? 'pending' : 'completed';
    const ok = await setActivityStatus(item.id, nextStatus);
    // Reading / logistics items mirror into learner_prep_tasks so the
    // coverage + logistics panels (and Milestones page) stay in sync.
    if (ok && item.linkedTask) {
      await setLogisticsDone(item.linkedTask, examId, nextStatus === 'completed');
    }
    setBusy(false);
    if (ok) {
      onActivityChange?.({
        ...item.raw,
        status: nextStatus,
        completed_at: nextStatus === 'completed' ? new Date().toISOString() : null,
      }, { linkedTask: item.linkedTask });
    }
  }

  return (
    <div className={`${styles.mini} ${completed ? styles.miniDone : ''}`} data-testid={`activity-${item.id}`}>
      <span className={`${styles.chip} ${styles['chip_' + item.taskType]}`}>{chip}</span>
      <div className={styles.taskBody}>
        <div className={styles.taskTitle}>{item.title}</div>
        {(meta || item.description) ? (
          <div className={styles.taskMeta}>{meta || item.description}</div>
        ) : null}
      </div>
      {completed ? <span className={styles.donePill}>Done</span> : null}
      {!completed && item.canStart ? (
        <button
          type="button"
          className={styles.btnSm}
          onClick={() => onStart?.(item.raw)}
          disabled={busy}
          data-testid={`activity-start-${item.id}`}
        >Start</button>
      ) : null}
      {item.taskType !== 'rest' && item.taskType !== 'exam_day' ? (
        <button
          type="button"
          className={`${styles.markBtn} ${completed ? styles.markDone : ''}`}
          onClick={toggle}
          disabled={busy}
          title={completed ? 'Mark as not done' : 'Mark complete'}
          data-testid={`activity-toggle-${item.id}`}
        >{completed ? '✓' : ''}</button>
      ) : null}
    </div>
  );
}
